/**
 * ObligationEditor — displays and edits the 4 fixed renewal obligation rows.
 *
 * DLD §8.1 — 4 fixed category rows: license, company, annual_filing, eor.
 * No free-form add/delete. Each row shows:
 *   - Category name (bold) + Next-due line + Schedule summary line
 *   - referenceNote / explanation / notes when present
 *   - Secondary filings when present
 *   - CompletionHistory
 *   - [Schedule ▾] collapsible CycleScheduleEditor (legal_advisor/admin only)
 *   - [Mark renewed] / [Amend date] buttons (legal_advisor/admin only)
 *
 * user role: all inputs read-only; action buttons hidden.
 *
 * Query key: ["entity-obligations", entityId]
 * Invalidates on mutation: same key + ["renewals"] + ["entity", entityId] +
 *   ["changelog-recent"] + ["entity-renewal-status", entityId]
 */

import { useState, useId } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import { RefreshCw, X, Info, Clock, ChevronDown, ChevronUp, CalendarClock } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { format as formatDate } from "date-fns";
import i18n from "../i18n";
import { cn } from "../lib/cn";
import {
  fetchObligations,
  markObligationRenewed,
  patchObligation,
} from "../lib/dataFetchers";
import { CycleScheduleEditor, type EntityForPreview } from "./CycleScheduleEditor";
import { Term } from "./ui/Term";
import { Tooltip } from "./ui/Tooltip";
import { RenderReferenceText } from "./ui/RenderReferenceText";
import { Button } from "./ui/Button";
import { SectionCard } from "./ui/SectionCard";
import type {
  Obligation,
  ObligationCategory,
  ObligationCompletion,
  ObligationSchedule,
} from "../lib/types";

// ─── Cycle label helper ───────────────────────────────────────────────────────
// Used for child (secondary-filing) obligation rows that may use either
// a standard "Ny" cycle string or a custom intervalNum/intervalUnit.

function childCycleLabel(obl: Obligation, t: TFunction): string | null {
  // Custom interval path: intervalNum + intervalUnit (e.g. Indonesia LKPM — 3 months)
  if (obl.intervalNum && obl.intervalUnit) {
    return t("child_cycle_interval", {
      num: obl.intervalNum,
      unit: obl.intervalUnit,
    });
  }

  // Standard cycle string: "1y"→"Every year", "2y"–"5y"→"Every N years"
  if (obl.cycle && typeof obl.cycle === "string" && obl.cycle !== "indefinite" && obl.cycle !== "manual") {
    const n = parseInt(obl.cycle, 10);
    if (!Number.isNaN(n)) {
      if (n === 1) return t("how_often_1y");
      return t(`how_often_${n}y`, { defaultValue: t("how_often_1y").replace("1", String(n)) });
    }
  }

  // Fallback: use the schedule cycle when present
  if (obl.schedule?.cycle?.kind === "simple" && obl.schedule.cycle.cycle) {
    const n = parseInt(obl.schedule.cycle.cycle, 10);
    if (!Number.isNaN(n)) {
      if (n === 1) return t("how_often_1y");
      return t(`how_often_${n}y`, { defaultValue: t("how_often_1y").replace("1", String(n)) });
    }
  }

  return null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** DLD §8.1 — exactly 4 fixed rows. "other" category is dropped. */
const FIXED_CATEGORIES: ObligationCategory[] = ["license", "company", "annual_filing", "eor"];

/** Keys invalidated after any obligation mutation. */
function invalidationKeys(entityId: string) {
  return [
    { queryKey: ["entity-obligations", entityId] },
    { queryKey: ["renewals"] },
    { queryKey: ["entity", entityId] },
    { queryKey: ["changelog-recent"] },
    { queryKey: ["entity-renewal-status", entityId] },
  ];
}

// ─── scheduleSummary helper ───────────────────────────────────────────────────
// Cycle-only phrasing — anchor/rule is not mentioned in the row summary.
// §4 — pure function, no network calls.

function scheduleSummary(schedule: ObligationSchedule | null | undefined, t: TFunction): string {
  if (!schedule) return t("schedule_summary_not_configured");

  const { cycle } = schedule;

  if (cycle.kind === "phased") return t("schedule_summary_phased");
  if (cycle.kind === "indefinite") return t("schedule_summary_indefinite");
  if (cycle.kind === "manual") return t("schedule_summary_manual");

  if (cycle.kind === "simple") {
    const n = cycle.cycle ? parseInt(cycle.cycle, 10) : 1;
    const years = Number.isNaN(n) ? 1 : n;
    if (years === 1) return t("schedule_summary_cycle_1y");
    return t("schedule_summary_cycle_ny", { count: years });
  }

  // fallback for any unlisted combo
  return t("schedule_summary_fallback");
}

// ─── Native date input ────────────────────────────────────────────────────────
// Used inside Radix dialogs where custom popover pickers don't surface reliably.

interface NativeDateInputProps {
  id?: string;
  value: string | null;
  onChange: (iso: string | null) => void;
  label: string;
  disabled?: boolean;
}

function NativeDateInput({ id, value, onChange, label, disabled }: NativeDateInputProps): JSX.Element {
  return (
    <input
      id={id}
      type="date"
      aria-label={label}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      className={cn(
        "h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-base",
        "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
      )}
    />
  );
}

// ─── Mark-renewed dialog ─────────────────────────────────────────────────────

interface MarkRenewedDialogProps {
  entityId: string;
  obligation: Obligation;
  open: boolean;
  onClose: () => void;
}

function MarkRenewedDialog({ entityId, obligation, open, onClose }: MarkRenewedDialogProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const { t: tCycles } = useTranslation("cycles");
  const qc = useQueryClient();
  const uid = useId();
  const [completedDate, setCompletedDate] = useState<string | null>(
    formatDate(new Date(), "yyyy-MM-dd"),
  );
  const [comment, setComment] = useState("");
  const commentId = `${uid}-comment`;
  const descId = `${uid}-mark-renewed-desc`;

  // BUG-9 fix: capture nextDueDate at dialog-open time so a background refetch
  // that updates the obligation prop does not silently change expectedCurrentNextDue,
  // causing a spurious 409 concurrency mismatch.
  const [capturedNextDue] = useState(() => obligation.nextDueDate ?? null);

  const cycleLabel = obligation.cycle && obligation.cycle !== "indefinite"
    ? tCycles(obligation.cycle)
    : null;

  const mutation = useMutation({
    mutationFn: () =>
      markObligationRenewed(entityId, obligation.id, {
        completedDate: completedDate ?? formatDate(new Date(), "yyyy-MM-dd"),
        expectedCurrentNextDue: capturedNextDue,
        comment: comment.trim() || null,
      }),
    onSuccess: (data) => {
      toast.success(t("mark_renewed_success").replace("{{date}}", data.nextDueDate ?? "updated"));
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      onClose();
    },
    onError: (err: unknown) => {
      const ax = err as {
        response?: {
          status?: number;
          data?: { code?: string; detail?: { code?: string } };
        };
      };
      const code =
        ax.response?.data?.detail?.code ?? ax.response?.data?.code;
      if (code === "indefinite_cycle") {
        toast.error(t("mark_renewed_error_indefinite"));
      } else if (ax.response?.status === 409) {
        toast.error(t("mark_renewed_error_conflict"));
      } else if (code === "not_confirmed") {
        toast.error(t("mark_renewed_error_not_confirmed"));
      } else {
        toast.error(t("mark_renewed_error"));
      }
    },
  });

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => { if (!v && !mutation.isPending) onClose(); if (!v) { setComment(""); setCompletedDate(formatDate(new Date(), "yyyy-MM-dd")); } }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/35" />
        <Dialog.Content
          aria-describedby={descId}
          className={cn(
            "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "w-[min(460px,90vw)] bg-surface rounded-[11px] border border-border shadow-pop p-6 focus:outline-none",
          )}
        >
          <Dialog.Title className="font-display font-semibold text-[15px] tracking-[-0.01em] text-text mb-1">
            {t("mark_renewed_title")}
          </Dialog.Title>
          <Dialog.Description id={descId} className="text-[13px] text-muted mb-5">
            {t("mark_renewed_desc")
              .replace("{{name}}", obligation.name)
              .replace("{{cycle}}", cycleLabel ?? "—")}
          </Dialog.Description>

          <div className="space-y-4">
            <div>
              <label htmlFor={`${uid}-completed-date`} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2">
                {t("mark_renewed_completed_label")}
              </label>
              <NativeDateInput
                id={`${uid}-completed-date`}
                value={completedDate}
                onChange={setCompletedDate}
                label={t("mark_renewed_completed_label")}
              />
            </div>
            <div>
              <label htmlFor={commentId} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2">
                {t("mark_renewed_comment_label")}
              </label>
              <input
                id={commentId}
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("mark_renewed_comment_placeholder")}
                className={cn(
                  "h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-base",
                  "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                )}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Dialog.Close asChild>
              <Button variant="secondary" disabled={mutation.isPending}>
                {t("cancel_button")}
              </Button>
            </Dialog.Close>
            <Button
              variant="primary"
              disabled={mutation.isPending || !completedDate}
              onClick={() => mutation.mutate()}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", mutation.isPending && "animate-spin")} aria-hidden />
              {mutation.isPending ? t("renewing") : t("mark_renewed_confirm")}
            </Button>
          </div>

          <Dialog.Close
            aria-label={t("dialog_close")}
            className="absolute right-3 top-3 h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-surface-2 text-dim focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20"
          >
            <X className="h-4 w-4" aria-hidden />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Amend date dialog ───────────────────────────────────────────────────────

interface AmendDateDialogProps {
  entityId: string;
  obligation: Obligation;
  open: boolean;
  onClose: () => void;
}

function AmendDateDialog({ entityId, obligation, open, onClose }: AmendDateDialogProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const qc = useQueryClient();
  const uid = useId();
  const [amendedDate, setAmendedDate] = useState<string | null>(
    obligation.nextDueDate ?? null,
  );
  const [comment, setComment] = useState("");
  const commentId = `${uid}-amend-comment`;
  const descId = `${uid}-amend-desc`;

  const mutation = useMutation({
    mutationFn: () =>
      patchObligation(entityId, obligation.id, {
        nextDueDate: amendedDate,
        status: "confirmed",
        comment: comment.trim(),
        ...(obligation.reviewFlag ? { reviewFlag: false } : {}),
      }),
    onSuccess: () => {
      toast.success(t("amend_date_success"));
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      onClose();
    },
    onError: (err: unknown) => {
      const ax = err as {
        response?: {
          status?: number;
          data?: { code?: string; detail?: { code?: string } };
        };
      };
      if (ax.response?.status === 409) {
        toast.error(t("mark_renewed_error_conflict"));
      } else {
        toast.error(t("amend_date_error"));
      }
    },
  });

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v && !mutation.isPending) onClose();
        if (!v) {
          setComment("");
          setAmendedDate(obligation.nextDueDate ?? null);
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/35" />
        <Dialog.Content
          aria-describedby={descId}
          className={cn(
            "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "w-[min(460px,90vw)] bg-surface rounded-[11px] border border-border shadow-pop p-6 focus:outline-none",
          )}
        >
          <Dialog.Title className="font-display font-semibold text-[15px] tracking-[-0.01em] text-text mb-1">
            {t("amend_date_title")}
          </Dialog.Title>
          <Dialog.Description id={descId} className="text-[13px] text-muted mb-4">
            {t("amend_date_desc").replace("{{name}}", obligation.name)}
          </Dialog.Description>

          <div className="space-y-4">
            <div>
              <label htmlFor={`${uid}-amend-date`} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2">
                {t("next_due_label")}
              </label>
              <NativeDateInput
                id={`${uid}-amend-date`}
                value={amendedDate}
                onChange={setAmendedDate}
                label={t("next_due_label")}
              />
            </div>
            <div>
              <label htmlFor={commentId} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2">
                {t("amend_date_comment_label")}
              </label>
              <input
                id={commentId}
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("amend_date_comment_placeholder")}
                className={cn(
                  "h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-base",
                  "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                )}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Dialog.Close asChild>
              <Button variant="secondary" disabled={mutation.isPending}>
                {t("cancel_button")}
              </Button>
            </Dialog.Close>
            <Button
              variant="primary"
              disabled={mutation.isPending || !amendedDate}
              onClick={() => mutation.mutate()}
            >
              <CalendarClock className={cn("h-3.5 w-3.5", mutation.isPending && "animate-spin")} aria-hidden />
              {mutation.isPending ? t("amend_date_saving") : t("amend_date_confirm")}
            </Button>
          </div>

          <Dialog.Close
            aria-label={t("dialog_close")}
            className="absolute right-3 top-3 h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-surface-2 text-dim focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20"
          >
            <X className="h-4 w-4" aria-hidden />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Completion history ───────────────────────────────────────────────────────

interface CompletionHistoryProps {
  completions: ObligationCompletion[];
}

function CompletionHistory({ completions }: CompletionHistoryProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const [expanded, setExpanded] = useState(false);
  const locale = i18n.language;

  if (completions.length === 0) {
    return (
      <div
        data-testid="completion-history"
        className="text-[11.5px] text-dim pt-1"
      >
        {t("completion_history_empty")}
      </div>
    );
  }

  const formatDisplayDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });

  const sorted = [...completions].sort(
    (a, b) => new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime(),
  );
  const visibleItems = expanded || sorted.length <= 3 ? sorted : sorted.slice(0, 2);

  return (
    <div data-testid="completion-history" className="space-y-1.5">
      <div className="flex items-center justify-between">
        <h4 className="text-[10px] font-bold text-dim uppercase tracking-[.05em]">
          {t("completion_history_title")}
        </h4>
        {sorted.length > 3 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-0.5 text-[11.5px] text-dim hover:text-text focus:outline-none"
            aria-expanded={expanded}
          >
            {expanded ? (
              <><ChevronUp className="h-3 w-3" aria-hidden /> {t("completion_show_less")}</>
            ) : (
              <><ChevronDown className="h-3 w-3" aria-hidden /> {t("completion_show_more", { count: sorted.length - 2 })}</>
            )}
          </button>
        )}
      </div>
      <ul className="space-y-1.5">
        {visibleItems.map((c) => (
          <li
            key={c.id}
            data-field="obligation.completion"
            className="rounded-lg bg-surface-2 border border-border-2 px-3 py-2 text-[11.5px] space-y-0.5"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Clock className="h-3 w-3 text-dim shrink-0" aria-hidden />
              <span className="font-semibold text-text">
                {t("completion_row_completed", { date: formatDisplayDate(c.completedDate) })}
              </span>
              {c.userName && (
                <span className="text-muted">
                  {t("completion_row_by", { name: c.userName })}
                </span>
              )}
            </div>
            {(c.previousNextDue || c.newNextDue) && (
              <div className="text-muted pl-5">
                {t("completion_row_due_change", {
                  prev: c.previousNextDue ?? "—",
                  next: c.newNextDue ?? "—",
                })}
                {c.cycleAtCompletion && (
                  <span className="ml-2 text-dim">
                    ({t("completion_row_cycle", { cycle: c.cycleAtCompletion })})
                  </span>
                )}
              </div>
            )}
            {c.comment && (
              <div className="text-muted pl-5 italic">
                {t("completion_row_comment", { comment: c.comment })}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Single obligation row ────────────────────────────────────────────────────

interface ObligationRowProps {
  entityId: string;
  obligation: Obligation;
  canEdit: boolean;
  entity?: EntityForPreview;
  /** Child obligations linked to this parent via parentObligationId. */
  childObligations?: Obligation[];
}

function ObligationRow({ entityId, obligation, canEdit, entity, childObligations = [] }: ObligationRowProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const qc = useQueryClient();
  const [markRenewedOpen, setMarkRenewedOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState(obligation.notes ?? "");
  const [notesComment, setNotesComment] = useState("");

  const notesMutation = useMutation({
    mutationFn: () =>
      patchObligation(entityId, obligation.id, {
        notes: notesDraft.trim() || null,
        comment: notesComment.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t("update_success"));
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      setNotesEditing(false);
      setNotesComment("");
    },
    onError: (err) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(msg || t("update_error"));
    },
  });

  const categoryLabels: Record<string, string> = {
    license:       t("cat_license"),
    company:       t("cat_company"),
    annual_filing: t("cat_annual_filing"),
    eor:           t("cat_eor"),
  };

  const isConfirmed   = obligation.status === "confirmed" || obligation.status === "estimated";
  const isNotRequired = obligation.status === "not_required";
  const isIndefinite  = obligation.status === "indefinite";
  const isManualOrPending = obligation.status === "needs_confirmation";

  const canMarkRenewed = canEdit && isConfirmed && !isIndefinite && !isNotRequired;

  // Suppress explanation when the obligation already has a usable next-due date
  // (the date + summary line already communicate what's needed). Only show when
  // there's no usable date — it becomes an actionable "needs X date" hint.
  const hasUsableDate = isConfirmed && Boolean(obligation.nextDueDate);
  const rawExplanationText = obligation.explanation
    ?? (isManualOrPending ? t("license_manual_note") : null);
  const explanationText = hasUsableDate ? null : rawExplanationText;

  // §4 — schedule summary line
  // C.2 fix: only show schedule summary for confirmed/estimated status — never for
  // not_required, indefinite, or needs_confirmation (prevents contradictory stacks).
  const summary = scheduleSummary(obligation.schedule, t);
  const showScheduleSummary = isConfirmed;

  return (
    <>
      <li
        className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-start px-[16px] py-[14px] border-b border-border-2 last:border-b-0"
        data-field={`obligation-${obligation.category}`}
      >
        {/* Left: name + next due + schedule summary + explanation + notes */}
        <div>
          {/* Category name */}
          <div className="flex items-center gap-[9px] flex-wrap">
            <span className="text-[14.5px] font-bold text-text">
              {obligation.category === "eor" ? (
                <Term term="eor">{categoryLabels["eor"] ?? "EOR"}</Term>
              ) : (
                categoryLabels[obligation.category] ?? obligation.category
              )}
            </span>
          </div>

          {/* Next due line */}
          <p className="text-[13px] text-label mt-[5px]">
            {isNotRequired ? (
              t("status_not_required")
            ) : isIndefinite ? (
              t("status_indefinite")
            ) : isConfirmed && obligation.nextDueDate ? (
              <>{t("next_due_label")}: <strong className="text-text font-semibold">{obligation.nextDueDate}</strong></>
            ) : (
              <span className="text-amber-text">{t("set_date_affordance")}</span>
            )}
          </p>

          {/* Schedule summary line — only for confirmed/estimated (C.2 fix) */}
          {showScheduleSummary && (
            <p className="text-[12.5px] text-muted mt-[4px]">
              {summary}
            </p>
          )}

          {/* Explanation */}
          {explanationText && (
            <p
              data-testid="obligation-explanation"
              className={cn(
                "inline-flex items-center gap-[6px] text-[12px] mt-[6px]",
                isManualOrPending ? "text-amber-text" : "text-muted",
              )}
            >
              <Info className="h-[13px] w-[13px] shrink-0" aria-hidden />
              <span><RenderReferenceText text={explanationText} /></span>
            </p>
          )}

          {/* Verbatim referenceNote — only if present AND not equal to summary or explanation */}
          {obligation.referenceNote
            && obligation.referenceNote !== rawExplanationText
            && obligation.referenceNote !== summary && (
            <p className="text-[11.5px] text-dim italic pl-1 mt-1">
              <RenderReferenceText text={obligation.referenceNote} />
            </p>
          )}

          {/* Notes — editable by admin/legal_advisor */}
          <div data-field="obligation.notes" className="mt-2">
            {notesEditing ? (
              <div className="space-y-2 rounded-lg bg-surface-2 border border-border px-2.5 py-2">
                <label className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim">
                  {t("notes_field_label")}
                </label>
                <textarea
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder={t("notes_placeholder")}
                  rows={3}
                  className={cn(
                    "w-full px-3 py-2 rounded-lg border border-border bg-surface text-[13px] text-base resize-none",
                    "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                  )}
                />
                <input
                  type="text"
                  value={notesComment}
                  onChange={(e) => setNotesComment(e.target.value)}
                  placeholder={t("mark_renewed_comment_placeholder")}
                  className={cn(
                    "h-9 w-full px-3 rounded-lg border border-border bg-surface text-base",
                    "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                  )}
                />
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    disabled={notesMutation.isPending}
                    onClick={() => notesMutation.mutate()}
                    className="h-[34px] text-[12.5px]"
                  >
                    {notesMutation.isPending ? t("cse_saving") : t("save_button")}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setNotesEditing(false);
                      setNotesDraft(obligation.notes ?? "");
                      setNotesComment("");
                    }}
                  >
                    {t("cancel_button")}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className={cn(
                  "group flex items-start gap-2 rounded-lg px-2.5 py-1.5",
                  obligation.notes
                    ? "bg-surface-2 border border-border-2"
                    : canEdit ? "border border-dashed border-border rounded-lg" : "",
                )}
              >
                <p className="flex-1 text-[11.5px] text-muted">
                  {obligation.notes ? (
                    <><span className="font-semibold mr-1">{t("notes_field_label")}:</span>{obligation.notes}</>
                  ) : canEdit ? (
                    <span className="text-dim italic">{t("notes_add_placeholder")}</span>
                  ) : null}
                </p>
                {canEdit && (
                  <button
                    type="button"
                    aria-label={t("edit_notes_aria")}
                    onClick={() => { setNotesDraft(obligation.notes ?? ""); setNotesEditing(true); }}
                    className={cn(
                      "shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity",
                      "inline-flex items-center justify-center h-[22px] w-[22px] rounded text-dim hover:text-accent",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
                    )}
                  >
                    <svg className="h-[12px] w-[12px]" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
                      <path d="M11.5 2.5l2 2-9 9H2.5v-2l9-9z" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Secondary filings */}
          {obligation.schedule?.secondary && obligation.schedule.secondary.length > 0 && (
            <div data-field="obligation.secondary" className="space-y-1 mt-2">
              <p className="text-[10px] font-bold text-dim uppercase tracking-[.05em]">
                {t("secondary_filings_section")}
              </p>
              <ul className="space-y-0.5">
                {obligation.schedule.secondary.map((sf, idx) => (
                  <li key={idx} className="text-[11.5px] text-muted pl-2 border-l-2 border-border">
                    <span className="font-semibold">{sf.name}</span>
                    {sf.cycle && <span className="ml-1 text-dim">({sf.cycle})</span>}
                    {sf.referenceNote && <span className="ml-1 italic">{sf.referenceNote}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional-filings hint — shown when there are child obligation rows */}
          {childObligations.length > 0 && (
            <p
              data-testid="additional-filing-hint"
              className="text-[11.5px] text-dim mt-2 pl-1"
            >
              {childObligations.length === 1
                ? t("additional_filing_hint_one", {
                    name: childObligations[0].name,
                    cycle: childCycleLabel(childObligations[0], t) ?? "—",
                  })
                : t("additional_filing_hint_other", {
                    count: childObligations.length,
                    name: childObligations[0].name,
                  })}
            </p>
          )}

          {/* Completion history */}
          <div className="border-t border-border-2 pt-3 mt-3">
            <CompletionHistory completions={obligation.completions} />
          </div>
        </div>

        {/* Right: cycle editor + action buttons (explanations on hover tooltip) */}
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          {canEdit && (
            <CycleScheduleEditor entityId={entityId} obligation={obligation} entity={entity} />
          )}
          {canEdit && canMarkRenewed && (
            <Tooltip content={t("mark_renewed_helper_short")} side="left">
              <Button
                variant="primary"
                onClick={() => setMarkRenewedOpen(true)}
                aria-label={`${t("mark_renewed_button")} — ${obligation.name}`}
                className="h-[34px] text-[12.5px]"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                {t("mark_renewed_button")}
              </Button>
            </Tooltip>
          )}
          {canEdit && !isNotRequired && !isIndefinite && (
            <Tooltip content={t("amend_date_helper_short")} side="left">
              <Button
                variant="secondary"
                onClick={() => setAmendOpen(true)}
                aria-label={`${t("amend_date_button_short")} — ${obligation.name}`}
                className="h-[34px] text-[12.5px]"
              >
                <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                {t("amend_date_button_short")}
              </Button>
            </Tooltip>
          )}
        </div>
      </li>

      <MarkRenewedDialog
        entityId={entityId}
        obligation={obligation}
        open={markRenewedOpen}
        onClose={() => setMarkRenewedOpen(false)}
      />
      <AmendDateDialog
        entityId={entityId}
        obligation={obligation}
        open={amendOpen}
        onClose={() => setAmendOpen(false)}
      />
    </>
  );
}

// ─── Child obligation row ─────────────────────────────────────────────────────
// Rendered below each parent ObligationRow for each child (secondary-filing)
// obligation linked to it. Compact, indented, actionable — no CycleScheduleEditor.

interface ChildObligationRowProps {
  entityId: string;
  child: Obligation;
  canEdit: boolean;
}

function ChildObligationRow({ entityId, child, canEdit }: ChildObligationRowProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const [markRenewedOpen, setMarkRenewedOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);

  const cycleLabel = childCycleLabel(child, t);

  const isConfirmed   = child.status === "confirmed" || child.status === "estimated";
  const isNotRequired = child.status === "not_required";
  const isIndefinite  = child.status === "indefinite";
  const canMarkRenewed = canEdit && isConfirmed && !isIndefinite && !isNotRequired;

  return (
    <>
      <li
        className={cn(
          "grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start",
          "pl-[34px] pr-[16px] py-[10px] border-b border-border-2 last:border-b-0",
          "border-l-2 border-l-accent/30",
        )}
        data-field={`obligation-child-${child.id}`}
        data-testid="child-obligation-row"
      >
        {/* Left: name + cycle + next-due */}
        <div>
          <div className="flex items-center gap-[7px] flex-wrap">
            {/* Indentation arrow */}
            <span className="text-dim text-[13px] shrink-0 select-none" aria-hidden>↳</span>
            <span className="text-[13.5px] font-semibold text-muted">
              {child.name}
            </span>
            {cycleLabel && (
              <span className="text-[12px] text-dim">· {cycleLabel}</span>
            )}
          </div>

          {/* Next due */}
          <p className="text-[12.5px] text-label mt-[4px] pl-[20px]">
            {isNotRequired ? (
              <span className="text-dim italic">{t("status_not_required")}</span>
            ) : isIndefinite ? (
              <span className="text-dim italic">{t("status_indefinite")}</span>
            ) : isConfirmed && child.nextDueDate ? (
              <>{t("next_due_label")}: <strong className="text-text font-semibold">{child.nextDueDate}</strong></>
            ) : (
              <span className="text-amber-text text-[12px]">{t("set_date_affordance")}</span>
            )}
          </p>
        </div>

        {/* Right: action buttons */}
        <div className="flex flex-row sm:flex-col items-stretch gap-2">
          {canMarkRenewed && (
            <Tooltip content={t("mark_renewed_helper_short")} side="left">
              <Button
                variant="primary"
                onClick={() => setMarkRenewedOpen(true)}
                aria-label={`${t("mark_renewed_button")} — ${child.name}`}
                className="h-[44px] text-[12px] min-h-[44px]"
              >
                <RefreshCw className="h-3 w-3" aria-hidden />
                {t("mark_renewed_button")}
              </Button>
            </Tooltip>
          )}
          {canEdit && !isNotRequired && !isIndefinite && (
            <Tooltip content={t("amend_date_helper_short")} side="left">
              <Button
                variant="secondary"
                onClick={() => setAmendOpen(true)}
                aria-label={`${t("amend_date_button_short")} — ${child.name}`}
                className="h-[44px] text-[12px] min-h-[44px]"
              >
                <CalendarClock className="h-3 w-3" aria-hidden />
                {t("amend_date_button_short")}
              </Button>
            </Tooltip>
          )}
        </div>
      </li>

      <MarkRenewedDialog
        entityId={entityId}
        obligation={child}
        open={markRenewedOpen}
        onClose={() => setMarkRenewedOpen(false)}
      />
      <AmendDateDialog
        entityId={entityId}
        obligation={child}
        open={amendOpen}
        onClose={() => setAmendOpen(false)}
      />
    </>
  );
}

// ─── Obligation row group (parent + children) ─────────────────────────────────
// Groups a parent ObligationRow with its ChildObligationRow children so that
// the list can map over FIXED_CATEGORIES with a single keyed element per category.

interface ObligationRowGroupProps {
  entityId: string;
  obligation: Obligation;
  canEdit: boolean;
  entity?: EntityForPreview;
  childObligations: Obligation[];
}

function ObligationRowGroup({
  entityId,
  obligation,
  canEdit,
  entity,
  childObligations,
}: ObligationRowGroupProps): JSX.Element {
  return (
    <>
      <ObligationRow
        entityId={entityId}
        obligation={obligation}
        canEdit={canEdit}
        entity={entity}
        childObligations={childObligations}
      />
      {childObligations.map((child) => (
        <ChildObligationRow
          key={child.id}
          entityId={entityId}
          child={child}
          canEdit={canEdit}
        />
      ))}
    </>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ObligationSkeleton(): JSX.Element {
  return (
    <ul className="divide-y divide-border-2">
      {Array.from({ length: 4 }, (_, i) => (
        <li key={i} className="px-[16px] py-[14px] space-y-2">
          <div className="skeleton h-4 w-32" />
          <div className="skeleton h-3 w-64" />
        </li>
      ))}
    </ul>
  );
}

// ─── Empty row placeholder ────────────────────────────────────────────────────

interface EmptyObligationRowProps {
  category: ObligationCategory;
}

function EmptyObligationRow({ category }: EmptyObligationRowProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const categoryLabels: Record<string, string> = {
    license:       t("cat_license"),
    company:       t("cat_company"),
    annual_filing: t("cat_annual_filing"),
    eor:           t("cat_eor"),
  };

  return (
    <li
      className="px-[16px] py-[14px] border-b border-border-2 last:border-b-0 opacity-50"
      data-field={`obligation-${category}`}
    >
      <div className="flex items-center gap-[9px]">
        <span className="text-[14.5px] font-bold text-muted">
          {categoryLabels[category]}
        </span>
        <span className="text-[12px] text-dim">{t("no_data_yet")}</span>
      </div>
    </li>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface ObligationEditorProps {
  entityId: string;
  canEdit: boolean;
  entity?: EntityForPreview;
}

export function ObligationEditor({ entityId, canEdit, entity }: ObligationEditorProps): JSX.Element {
  const { t } = useTranslation("obligations");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["entity-obligations", entityId],
    queryFn: () => fetchObligations(entityId),
    enabled: Boolean(entityId),
  });

  const obligations = data?.obligations ?? [];

  // Parent obligations: those WITHOUT a parentObligationId, belonging to a fixed category.
  // This prevents an empty child obligation (e.g. Japan "Directors re-registration") from
  // overwriting the real parent in the byCategory map.
  const byCategory = new Map<ObligationCategory, Obligation>();
  for (const obl of obligations) {
    if (FIXED_CATEGORIES.includes(obl.category) && !obl.parentObligationId) {
      // First-wins: if there are somehow multiple parents for a category, keep the first.
      if (!byCategory.has(obl.category)) {
        byCategory.set(obl.category, obl);
      }
    }
  }

  // Children: obligations with parentObligationId set, keyed by their parent's id.
  const childrenByParent = new Map<string, Obligation[]>();
  for (const obl of obligations) {
    if (obl.parentObligationId) {
      const siblings = childrenByParent.get(obl.parentObligationId) ?? [];
      siblings.push(obl);
      childrenByParent.set(obl.parentObligationId, siblings);
    }
  }

  return (
    <SectionCard
      title={t("section_title")}
      hint={t("renewal_obligations_subtitle")}
      bodyClassName="p-0"
    >
      {isError ? (
        <p className="text-[13px] text-muted px-[16px] py-3">{t("load_error")}</p>
      ) : isLoading ? (
        <ObligationSkeleton />
      ) : (
        <ul>
          {FIXED_CATEGORIES.map((cat) => {
            const obl = byCategory.get(cat);
            if (!obl) {
              return <EmptyObligationRow key={cat} category={cat} />;
            }
            const children = childrenByParent.get(obl.id) ?? [];
            return (
              <ObligationRowGroup
                key={obl.id}
                entityId={entityId}
                obligation={obl}
                canEdit={canEdit}
                entity={entity}
                childObligations={children}
              />
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}
